// Doet een fetch met een "head-start" venster: we gunnen de request een paar
// seconden om een directe netwerkfout (DNS-fail, CORS, connection reset) of een
// snelle serverfout te laten opduiken. Blijft de fetch na dat venster hangen,
// dan is de server hem aan het verwerken en gaan we verder met database-polling.
//
// Nieuw t.o.v. de oude inline-variant: een snelle netwerk-reject wordt één keer
// stil opnieuw geprobeerd. Zo'n reject betekent dat de request de browser nooit
// heeft verlaten, dus opnieuw proberen is veilig (geen dubbele server-verwerking)
// en vangt losse verbindings-haperingen weg vóór de gebruiker een fout ziet.

export interface HeadStartFetchResult {
  // Gezet als de fetch (na eventuele retries) alsnog met een netwerkfout faalde.
  networkError: Error | null;
  // Gezet als de response binnen het head-start venster terugkwam. Blijft null
  // als de fetch nog in-flight is na het venster (dan volgt polling).
  response: Response | null;
}

export interface HeadStartRetryOptions {
  headStartMs?: number;
  maxRetries?: number;
  backoffMs?: number;
}

const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export async function fetchWithHeadStartRetry(
  input: string,
  init: RequestInit,
  opts: HeadStartRetryOptions = {},
): Promise<HeadStartFetchResult> {
  const { headStartMs = 3000, maxRetries = 1, backoffMs = 1500 } = opts;

  for (let attempt = 0; ; attempt++) {
    const state: { networkError: Error | null; response: Response | null } = {
      networkError: null,
      response: null,
    };

    const fetchPromise = fetch(input, init)
      .then((res: Response) => {
        state.response = res;
      })
      .catch((err: unknown) => {
        state.networkError = err instanceof Error ? err : new Error(String(err));
      });

    await Promise.race([fetchPromise, delay(headStartMs)]);

    // Snelle netwerk-reject binnen het venster: de request is nooit vertrokken,
    // dus stil één keer opnieuw proberen na een korte backoff.
    if (state.networkError && attempt < maxRetries) {
      await delay(backoffMs);
      continue;
    }

    return { networkError: state.networkError, response: state.response };
  }
}
